import React from "react";

interface Capability {
	title: string;
	description: string;
}

interface WhoWeAreCapabilitiesProps {
	headline?: string;
	description?: string;
	capabilitiesTitle?: string;
	capabilities?: Capability[];
	tagline?: string;
}

const DEFAULT_CAPABILITIES: Capability[] = [
	{
		title: "Product Leadership",
		description: "Roadmaps, technical strategy, phased builds, architecture, prioritization.",
	},
	{
		title: "UX & Digital Experience",
		description: "Design systems, UI/UX, interaction patterns, accessibility, user flows.",
	},
	{
		title: "Engineering",
		description: "Web apps, mobile apps, backend systems, integrations, infrastructure, performance.",
	},
	{
		title: "AI-Native Execution",
		description: "Modern development workflows that dramatically increase velocity without sacrificing quality.",
	},
	{
		title: "QA & Reliability",
		description: "Ticketed process, automated + manual testing, device coverage, clean releases.",
	},
	{
		title: "Ongoing Partnership",
		description: "Continuous improvement, analytics, iteration, and long-term digital stewardship.",
	},
];

const WhoWeAreCapabilities: React.FC<WhoWeAreCapabilitiesProps> = ({
	headline = "A Development Partner Built for Today's Software Challenges",
	description = "Software now needs to be beautiful, intuitive, fast, and constantly evolving. Most teams can't keep up — not because they aren't smart, but because the work now spans product strategy, UX, engineering, DevOps, QA, and AI-accelerated workflows.\n\nCrafted brings all of that together in one senior, scalable team.",
	capabilitiesTitle = "What We Do",
	capabilities = DEFAULT_CAPABILITIES,
	tagline = "One partner. Every capability you need. Delivered with discipline, taste, and velocity.",
}) => {
	return (
		<div className="bg-white py-20 md:py-28">
			<div className="container mx-auto px-4 max-w-6xl">
				{/* Header */}
				<div className="max-w-4xl mb-16">
					<h2 className="text-[clamp(28px,3vw,42px)] font-bold font-display leading-tight mb-6">
						{headline}
					</h2>
					<div className="text-[clamp(16px,1.4vw,20px)] text-gray-700 leading-relaxed whitespace-pre-line">
						{description}
					</div>
				</div>

				{/* Capabilities Grid */}
				<div className="mb-16">
					<h3 className="text-xl font-bold font-display mb-8 text-gray-900">
						{capabilitiesTitle}
					</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
						{capabilities.map((capability, index) => (
							<div
								key={index}
								className="bg-gray-50 rounded-lg p-6 border border-gray-100"
							>
								<h4 className="text-lg font-bold font-display mb-2 text-gray-900">
									{capability.title}
								</h4>
								<p className="text-gray-600 leading-relaxed">
									{capability.description}
								</p>
							</div>
						))}
					</div>
				</div>

				{/* Tagline */}
				<div className="border-t border-gray-200 pt-8">
					<p className="text-[clamp(18px,1.8vw,26px)] font-display font-bold text-gray-900 leading-snug">
						{tagline}
					</p>
				</div>
			</div>
		</div>
	);
};

export default WhoWeAreCapabilities;
